/**
 * レビュー画面の工程ごとの修正(#219)。
 *
 * レビュー中は本文を見ながら確認事項に答えているので、「1cmに切る → 5cmに切る」のような
 * 直しもその場で入れられるようにする。記録は「マニュアルを編集」(#203)と同じ manualEdits で、
 * 本文は「回答を本文に反映」で resolve が当てる(画面は依頼を記録するだけ)。
 */
import { useState } from "react";
import { deleteDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import { editDocId, stepFieldValue, type EditDoc, type EditableStepFields } from "../lib/manual-edits";

interface Props {
  dishId: string;
  /** 本文の工程(反映前の値。編集はここからの差分として持つ) */
  step: EditableStepFields;
  /** 工程の鍵(start_end) */
  stepKey: string;
  /** 記録済みの編集(manualEdits 全件) */
  edits: Record<string, EditDoc>;
  /** 最後の工程なら盛り付け基準も直せる(レビュー画面では最後の工程に出している) */
  plating?: string | null;
  onClose: () => void;
}

interface FieldDef {
  target: "step" | "dish";
  key: string;
  field: string;
  label: string;
  current: string;
  rows?: number;
  placeholder?: string;
}

export function StepEditPanel({ dishId, step, stepKey, edits, plating, onClose }: Props) {
  const { user, paths } = useAuth();
  const { t } = useI18n();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const s = (field: string, label: string, rows?: number, placeholder?: string): FieldDef => ({
    target: "step",
    key: stepKey,
    field,
    label,
    current: stepFieldValue(step, field),
    rows,
    placeholder,
  });
  const fields: FieldDef[] = [
    s("name", t("工程名")),
    s("actions", t("作業内容"), 4),
    s("heat_level", t("火加減"), undefined, t("例: 強火")),
    s("tools", t("使用器具（1行に1つ）"), 2),
    s("critical_points", t("重要ポイント（1行に1つ）"), 3),
    s("common_mistakes", t("ありがちなミス（1行に1つ）"), 3),
    s("completion_criteria", t("完了基準"), 2),
  ];
  if (plating !== undefined) {
    fields.push({ target: "dish", key: "", field: "plating_criteria", label: t("盛り付け基準"), current: plating ?? "", rows: 3 });
  }

  const idOf = (f: FieldDef) => editDocId(f.target, f.key, f.field);
  const valueOf = (f: FieldDef) => drafts[idOf(f)] ?? edits[idOf(f)]?.value ?? f.current;
  const dirty = fields.filter((f) => drafts[idOf(f)] !== undefined && drafts[idOf(f)] !== (edits[idOf(f)]?.value ?? f.current));

  async function save() {
    if (!user || !paths || dirty.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const batch = writeBatch(db);
      for (const f of dirty) {
        const ref = paths.manualEditDoc(dishId, idOf(f));
        const value = drafts[idOf(f)];
        // 元の本文と同じに戻したら記録そのものを消す(差分だけを持つ。#203 と同じ)
        if (value === f.current) batch.delete(ref);
        else
          batch.set(ref, {
            target: f.target,
            key: f.key,
            field: f.field,
            value,
            editedBy: user.uid,
            editedAt: serverTimestamp(),
          });
      }
      await batch.commit();
      setDrafts({});
      onClose();
    } catch (e) {
      setError(`${t("保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  async function revert(f: FieldDef) {
    if (!paths) return;
    setSaving(true);
    try {
      await deleteDoc(paths.manualEditDoc(dishId, idOf(f)));
      setDrafts((d) => {
        const next = { ...d };
        delete next[idOf(f)];
        return next;
      });
    } catch (e) {
      setError(`${t("取り消しに失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card step-edit">
      <p className="muted small">
        {t("直した内容は保存すると記録され、品目の画面の「回答を本文に反映」でマニュアルに反映されます。作り直しても引き継がれます。")}
      </p>
      {fields.map((f) => {
        const id = idOf(f);
        const edited = !!edits[id];
        const isDirty = dirty.includes(f);
        return (
          <div className={`edit-field${edited ? " edited" : ""}${isDirty ? " dirty" : ""}`} key={id}>
            <label>
              {f.label}
              {edited && <span className="badge p-medium">{t("編集済み")}</span>}
              {edited && (
                <button className="link" disabled={saving} onClick={() => void revert(f)}>
                  {t("元に戻す")}
                </button>
              )}
            </label>
            {f.rows && f.rows > 1 ? (
              <textarea
                rows={f.rows}
                value={valueOf(f)}
                placeholder={f.placeholder}
                onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
              />
            ) : (
              <input
                type="text"
                value={valueOf(f)}
                placeholder={f.placeholder}
                onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
              />
            )}
          </div>
        );
      })}
      {error && <p className="error">{error}</p>}
      <div className="step-edit-actions">
        <button onClick={() => void save()} disabled={saving || dirty.length === 0}>
          {saving ? t("保存中…") : t("修正を保存")}
        </button>
        <button className="link" onClick={onClose} disabled={saving}>
          {dirty.length > 0 ? t("保存せずに閉じる") : t("閉じる")}
        </button>
      </div>
    </div>
  );
}
